// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEventHeader, useTeamList, useHotelProperties, useMarkExempt, useUpdateAssignment, useUpdateTeamZip, TeamRow } from './hooks/useEventTeamTracker';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertTriangle, Copy, Check, MessageSquare, Plus, Send, Pencil, ShieldCheck, Ban, Eye } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { AddBookingModal } from './AddBookingModal';
import { ViewEditBookingModal } from './ViewEditBookingModal';
import { TeamNotesDrawer } from './TeamNotesDrawer';
import { UnlinkedRoomBlocks } from './UnlinkedRoomBlocks';
import { useRoomBlocks } from './hooks/useRoomBlocks';
import { isPartialPickup, isReleaseSoon, staybookNightsByTeam } from './roomBlockUtils';
import { formatCalendarDate } from '@/lib/dates';
import { Progress } from '@/components/ui/progress';

type FilterTab = 'all' | 'unbooked' | 'confirmed' | 'pending' | 'exempt' | 'cancelled' | 'partial' | 'local' | 'nozip';

const statusColors: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-800 border-green-300',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  exempt: 'bg-gray-100 text-gray-800 border-gray-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300',
  unbooked: 'bg-red-50 text-red-700 border-red-300',
};

const statusLabels: Record<string, string> = {
  confirmed: 'Confirmed',
  pending: 'Pending',
  exempt: 'Exempt',
  cancelled: 'Cancelled',
  unbooked: 'Unbooked',
};

const EventTeamTracker: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id;

  const { data: header, isLoading: headerLoading } = useEventHeader(eventId);
  const { data: teams, isLoading: teamsLoading } = useTeamList(eventId);
  const { data: hotels } = useHotelProperties(eventId);
  const blocks = useRoomBlocks(eventId ? [eventId] : []);
  const nightsByTeam = useMemo(() => staybookNightsByTeam(blocks.data || []), [blocks.data]);
  const markExempt = useMarkExempt(eventId);
  const updateAssignment = useUpdateAssignment();
  const updateTeamZip = useUpdateTeamZip(eventId);

  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);
  const [notesTeam, setNotesTeam] = useState<TeamRow | null>(null);
  const [bookingTeam, setBookingTeam] = useState<TeamRow | null>(null);
  const [editBookingTeam, setEditBookingTeam] = useState<TeamRow | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const filtered = useMemo(() => {
    if (!teams) return [];
    let list = teams;
    if (activeTab !== 'all') {
      if (activeTab === 'partial') list = list.filter(isPartialPickup);
      else if (activeTab === 'local') list = list.filter((t) => t.hotel_radius_exempt);
      else if (activeTab === 'nozip') list = list.filter((t) => !t.team_home_zip);
      else if (activeTab === 'cancelled') list = list.filter((t) => t.hotel_status === 'cancelled');
      else list = list.filter((t) => t.hotel_status === activeTab);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (t) =>
          t.team_name.toLowerCase().includes(q) ||
          t.coach_name.toLowerCase().includes(q) ||
          t.coach_email.toLowerCase().includes(q) ||
          (t.confirmation_number || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [teams, activeTab, search]);

  const counts = useMemo(() => {
    if (!teams) return { all: 0, unbooked: 0, confirmed: 0, pending: 0, exempt: 0, cancelled: 0, partial: 0, local: 0, nozip: 0 };
    return {
      all: teams.length,
      partial: teams.filter(isPartialPickup).length,
      unbooked: teams.filter((t) => t.hotel_status === 'unbooked').length,
      confirmed: teams.filter((t) => t.hotel_status === 'confirmed').length,
      pending: teams.filter((t) => t.hotel_status === 'pending').length,
      exempt: teams.filter((t) => t.hotel_status === 'exempt').length,
      cancelled: teams.filter((t) => t.hotel_status === 'cancelled').length,
      local: teams.filter((t) => t.hotel_radius_exempt).length,
      nozip: teams.filter((t) => !t.team_home_zip).length,
    };
  }, [teams]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCopyBookingUrl = () => {
    if (header?.booking_url) {
      navigator.clipboard.writeText(header.booking_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleMarkExempt = async () => {
    if (!eventId || !userId || selected.size === 0) return;
    try {
      await markExempt.mutateAsync({ registrationIds: Array.from(selected), eventIdVal: eventId, userId });
      toast.success(`${selected.size} team(s) marked as exempt`);
      setSelected(new Set());
    } catch {
      toast.error('Failed to mark teams as exempt');
    }
  };

  const handleSendReminder = () => {
    navigate('/hotel-portal/outreach', { state: { eventId } });
  };

  const startEdit = (field: string, value: string | null) => {
    setEditingField(field);
    setEditValue(value || '');
  };

  const saveEdit = async () => {
    if (!header || !editingField) return;
    try {
      await updateAssignment.mutateAsync({
        assignmentId: header.assignment_id,
        field: editingField,
        value: editValue || null,
      });
      toast.success('Updated');
      setEditingField(null);
    } catch {
      toast.error('Failed to update');
    }
  };

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'partial', label: 'Partial pickup' },
    { key: 'unbooked', label: 'Unbooked' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'pending', label: 'Pending' },
    { key: 'exempt', label: 'Exempt' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'local', label: 'Local' },
    { key: 'nozip', label: 'No home ZIP' },
  ];

  if (headerLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (!header) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Event not found or you are not assigned to it.
      </div>
    );
  }

  const pickupPct =
    header.rooms_contracted > 0
      ? Math.round((header.rooms_booked / header.rooms_contracted) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/hotel-portal/tracker')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{header.event_name}</h1>
          <p className="text-sm text-muted-foreground">
            {header.start_date ? format(parseISO(header.start_date), 'MMM d, yyyy') : '—'}
            {header.end_date && header.end_date !== header.start_date
              ? ` – ${format(parseISO(header.end_date), 'MMM d, yyyy')}`
              : ''}
            {' · '}
            {header.venue_name || ''}
            {header.venue_city ? `, ${header.venue_city}` : ''}
            {header.venue_state ? `, ${header.venue_state}` : ''}
          </p>
        </div>
      </div>

      {/* Attrition banner */}
      {header.attrition_risk && (
        <Card className="border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20">
          <CardContent className="flex items-center gap-3 py-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Attrition Risk: {pickupPct}% pickup — contract threshold not met
            </p>
          </CardContent>
        </Card>
      )}

      {/* Info bar */}
      <Card>
        <CardContent className="py-4 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
          {/* Booking URL */}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground font-medium">Booking URL:</span>
            {editingField === 'booking_url' ? (
              <div className="flex items-center gap-1">
                <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="h-7 w-64 text-xs" />
                <Button size="sm" variant="ghost" onClick={saveEdit}><Check className="h-3 w-3" /></Button>
              </div>
            ) : (
              <>
                <span className="truncate max-w-[200px]">{header.booking_url || 'Not set'}</span>
                {header.booking_url && (
                  <Button size="sm" variant="ghost" onClick={handleCopyBookingUrl}>
                    {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => startEdit('booking_url', header.booking_url)}>
                  <Pencil className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>

          {/* Group Rate Expiry */}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground font-medium">Group Rate Expiry:</span>
            {editingField === 'group_rate_expiry' ? (
              <div className="flex items-center gap-1">
                <Input type="date" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="h-7 w-36 text-xs" />
                <Button size="sm" variant="ghost" onClick={saveEdit}><Check className="h-3 w-3" /></Button>
              </div>
            ) : (
              <>
                <span>
                  {header.group_rate_expiry
                    ? format(parseISO(header.group_rate_expiry), 'MMM d, yyyy')
                    : 'Not set'}
                </span>
                <Button size="sm" variant="ghost" onClick={() => startEdit('group_rate_expiry', header.group_rate_expiry)}>
                  <Pencil className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>

          <span className="text-muted-foreground">
            <strong className="text-foreground">{header.hotel_count}</strong> hotel(s) contracted
          </span>

          {header.hotel_exempt_radius_miles != null && (
            <span className="text-muted-foreground">
              Local radius: <strong className="text-foreground">{header.hotel_exempt_radius_miles} mi</strong>
            </span>
          )}

          {header.rooms_contracted > 0 && (
            <span className="text-muted-foreground">
              Rooms: <strong className="text-foreground">{header.rooms_booked}</strong> booked / {header.rooms_contracted} contracted
              {' '}({pickupPct}%)
            </span>
          )}
        </CardContent>
      </Card>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <Button
            key={t.key}
            variant={activeTab === t.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setActiveTab(t.key); setSelected(new Set()); }}
          >
            {t.label} ({counts[t.key]})
          </Button>
        ))}
      </div>

      {/* Search */}
      <Input
        placeholder="Search team name, coach, or confirmation #..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>Team</TableHead>
                <TableHead>Coach</TableHead>
                <TableHead>Division</TableHead>
                <TableHead>Distance</TableHead>
                <TableHead>Hotel Status</TableHead>
                <TableHead>Rooms</TableHead>
                <TableHead>Release</TableHead>
                <TableHead>Hotel / Conf#</TableHead>
                <TableHead>Check-in</TableHead>
                <TableHead>Last Emailed</TableHead>
                <TableHead className="w-12 text-center">Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamsLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 13 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                    No teams match this filter.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((team) => (
                  <TableRow key={team.registration_id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(team.registration_id)}
                        onCheckedChange={() => toggleSelect(team.registration_id)}
                        disabled={team.hotel_status === 'confirmed' || team.hotel_status === 'exempt'}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{team.team_name}</TableCell>
                    <TableCell>
                      <div>{team.coach_name}</div>
                      <div className="text-xs text-muted-foreground">{team.coach_email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{team.division || team.age_group || '—'}</div>
                    </TableCell>
                    <TableCell>
                      <ZipCell
                        team={team}
                        onSave={(zip) =>
                          updateTeamZip.mutateAsync({ registrationId: team.registration_id, zip })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusColors[team.hotel_status]}>
                        {team.hotel_status === 'exempt' && team.booking_source === 'radius'
                          ? 'Exempt · Local'
                          : statusLabels[team.hotel_status]}
                        {team.booking_source === 'room_block' && <span className="ml-2 border-l border-current pl-2">Block</span>}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {team.block_count > 0 ? (
                        Number(team.rooms_requested) > 0 ? <div className="min-w-28 space-y-1">
                          <p className="text-sm whitespace-nowrap">{team.rooms_picked_up} / {team.rooms_requested} ({team.pickup_pct}%)</p>
                          <Progress value={Math.min(100, Math.max(0, team.pickup_pct))} className="h-1.5" aria-label={`${team.team_name} room pickup`} />
                        </div>
                        : nightsByTeam[team.registration_id] > 0
                          ? <p className="text-sm whitespace-nowrap">{nightsByTeam[team.registration_id]} room nights</p>
                          : '—'
                      ) : '—'}
                    </TableCell>
                    <TableCell className={`text-sm whitespace-nowrap ${isReleaseSoon(team.next_release_date) ? 'text-destructive font-medium' : ''}`}>
                      {formatCalendarDate(team.next_release_date, 'MMM d, yyyy', '—')}
                    </TableCell>
                    <TableCell>
                      {team.hotel_name ? (
                        <div>
                          <div className="text-sm">{team.hotel_name}</div>
                          {team.confirmation_number && (
                            <div className="text-xs text-muted-foreground">#{team.confirmation_number}</div>
                          )}
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {team.check_in_date ? format(parseISO(team.check_in_date), 'MMM d') : '—'}
                      {team.check_out_date ? ` – ${format(parseISO(team.check_out_date), 'MMM d')}` : ''}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {team.last_emailed
                        ? format(parseISO(team.last_emailed), 'MMM d, yyyy')
                        : 'Never'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="relative"
                        onClick={() => setNotesTeam(team)}
                      >
                        <MessageSquare className="h-4 w-4" />
                        {team.notes_count > 0 && (
                          <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full text-[10px] w-4 h-4 flex items-center justify-center">
                            {team.notes_count}
                          </span>
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {team.booking_id ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditBookingTeam(team)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View/Edit
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setBookingTeam(team)}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Booking
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {eventId && <UnlinkedRoomBlocks eventId={eventId} teams={teams || []} />}

      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-card border-t p-4 flex items-center gap-3 z-50">
          <span className="text-sm font-medium">{selected.size} team(s) selected</span>
          <Button size="sm" onClick={handleMarkExempt} disabled={markExempt.isPending}>
            <ShieldCheck className="h-4 w-4 mr-1" /> Mark Exempt
          </Button>
          <Button size="sm" variant="outline" onClick={handleSendReminder}>
            <Send className="h-4 w-4 mr-1" /> Send Reminder
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            <Ban className="h-4 w-4 mr-1" /> Cancel
          </Button>
        </div>
      )}

      {/* Modals */}
      {bookingTeam && (
        <AddBookingModal
          open={!!bookingTeam}
          onClose={() => setBookingTeam(null)}
          team={bookingTeam}
          eventId={eventId!}
          hotels={hotels ?? []}
          userId={userId!}
        />
      )}

      {editBookingTeam && (
        <ViewEditBookingModal
          open={!!editBookingTeam}
          onClose={() => setEditBookingTeam(null)}
          team={editBookingTeam}
          eventId={eventId!}
        />
      )}

      {notesTeam && (
        <TeamNotesDrawer
          open={!!notesTeam}
          onClose={() => setNotesTeam(null)}
          team={notesTeam}
          eventId={eventId!}
          userId={userId!}
          userName={
            [user?.user_metadata?.first_name, user?.user_metadata?.last_name]
              .filter(Boolean)
              .join(' ') || 'Partner'
          }
        />
      )}
    </div>
  );
};

export default EventTeamTracker;

// Distance cell: "18.6 mi" with a green Local tag when the team is inside the
// radius; "No ZIP" with an inline editor (save on Enter) when it isn't set.
// Saving writes event_registrations_v2.team_home_zip and the database
// recomputes distance and exemption, then the row refreshes.
function ZipCell({ team, onSave }: { team: TeamRow; onSave: (zip: string) => Promise<unknown> }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  if (team.team_home_zip) {
    return (
      <div className="flex items-center gap-2 whitespace-nowrap">
        <span className="text-sm">
          {team.distance_to_venue_miles != null
            ? `${Number(team.distance_to_venue_miles).toFixed(1)} mi`
            : '—'}
        </span>
        {team.hotel_radius_exempt && (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
            Local
          </Badge>
        )}
      </div>
    );
  }

  const save = async () => {
    const zip = value.trim();
    if (!/^\d{5}$/.test(zip)) {
      toast.error('Enter a 5-digit ZIP code');
      return;
    }
    setSaving(true);
    try {
      await onSave(zip);
      toast.success('ZIP saved');
    } catch {
      toast.error('Failed to save ZIP');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-muted-foreground whitespace-nowrap">No ZIP</span>
      <Input
        value={value}
        inputMode="numeric"
        maxLength={5}
        placeholder="ZIP"
        aria-label={`Home ZIP for ${team.team_name}`}
        className="h-7 w-20 text-xs"
        onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 5))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
          }
        }}
        disabled={saving}
      />
    </div>
  );
}
